import { MULTIPLE_CHOICE_ID } from 'Constants/General';
import { useQuestionnaire } from '../../Hooks/useQuestionnaire';
import { EvaluationAnswerFormT, LessonQuestionI, TestConfigurationI } from 'types';
import QuestionnaireHeader from '../QuestionnaireHeader/QuestionnaireHeader';
import QuestionnaireOptions from '../QuestionnaireOptions/QuestionnaireOptions';
import QuestionnairePagination from '../QuestionnairePagination/QuestionnairePagination';
import QuestionnaireFreeResponse from '../QuestionnaireFreeResponse/QuestionnaireFreeResponse';
import QuestionCounter from '../QuestionCounter/QuestionCounter';
import { QuestionnaireForm } from 'Pages/TestStart/test-start.styled';
import { Box, Typography } from '@mui/material';
import QuestionnaireConfirmationModal from '../QuestionnaireConfirmationModal/QuestionnaireConfirmationModal';
import QuestionnaireTimer from '../QuestionnaireTimer/QuestionnaireTimer';
import {
  SubmitButton,
  QuestionnaireCard,
  QuestionnaireWrapper,
  QuestionnaireContent,
  QuestionnaireProgress,
} from './Questionnaire.styled';

interface Props {
  questions: Array<LessonQuestionI>;
  onSubmit: (data: EvaluationAnswerFormT) => void;
  configuration: TestConfigurationI;
}

const Questionnaire = ({ questions, onSubmit, configuration }: Props) => {
  const {
    t2,
    open,
    errors,
    control,
    isDirty,
    answers,
    register,
    openModal,
    closeModal,
    isLastQuestion,
    isFirstQuestion,
    currentQuestion,
    handleNextQuestion,
    handleQuestionSelect,
    currentQuestionIndex,
    handlePreviousQuestion,
  } = useQuestionnaire({ questions });

  const progress = ((currentQuestionIndex + 1) / questions.length) * 100;

  return (
    <QuestionnaireWrapper>
      <QuestionnaireTimer duration={configuration?.duration} onTimeUp={() => onSubmit(answers)} />
      <QuestionnaireCard elevation={3}>
        <QuestionnaireForm>
          <QuestionnaireProgress variant="determinate" value={progress} />
          {currentQuestion ? (
            <>
              <QuestionCounter currentQuestionIndex={currentQuestionIndex} questions={questions} />
              <QuestionnaireHeader question={currentQuestion.question} />
              <QuestionnaireContent>
                {currentQuestion.questionType === MULTIPLE_CHOICE_ID ? (
                  <QuestionnaireOptions
                    options={currentQuestion.options || []}
                    key={currentQuestion.questionId}
                    register={register}
                    control={control}
                    name={`answers.${currentQuestionIndex}.answer`}
                    error={errors?.answers?.[currentQuestionIndex]}
                    currentValue={answers[currentQuestionIndex]?.answer}
                  />
                ) : (
                  <QuestionnaireFreeResponse
                    key={currentQuestion.questionId}
                    register={register}
                    control={control}
                    name={`answers.${currentQuestionIndex}.answer`}
                    error={errors?.answers?.[currentQuestionIndex]}
                    currentValue={answers[currentQuestionIndex]?.answer}
                  />
                )}
              </QuestionnaireContent>
            </>
          ) : (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography>Cargando preguntas...</Typography>
            </Box>
          )}
          <QuestionnairePagination
            handlePreviousQuestion={handlePreviousQuestion}
            handleNextQuestion={handleNextQuestion}
            handleQuestionSelect={handleQuestionSelect}
            currentQuestionIndex={currentQuestionIndex}
            isFirstQuestion={isFirstQuestion}
            isLastQuestion={isLastQuestion}
            questions={questions}
            answers={answers}
          />
          {isDirty && (
            <SubmitButton onClick={openModal} type="button">
              {t2('submit')}
            </SubmitButton>
          )}
        </QuestionnaireForm>
      </QuestionnaireCard>
      <QuestionnaireConfirmationModal open={open} closeModal={closeModal} handleSubmit={() => onSubmit(answers)} />
    </QuestionnaireWrapper>
  );
};

export default Questionnaire;
